import React, { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { contactPageData, socialMediaLinks, greeting } from "../portfolio";
import { blurIn, staggerContainer } from "../utils/motion";
import { FaGithub, FaLinkedinIn, FaInstagram } from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import ScrambleText from "./ScrambleText";
import { playKeyClick, playSubmit } from "../utils/soundEngine";

function SpotlightInput({ as: Tag = "input", className, onKeyDown, ...props }) {
  const ref = useRef(null);
  const [glow, setGlow] = useState({ x: 50, y: 50, active: false });

  const handleMove = useCallback((e) => {
    if (!ref.current) return;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    setGlow({
      x: ((e.clientX - left) / width) * 100,
      y: ((e.clientY - top) / height) * 100,
      active: true,
    });
  }, []);

  const handleLeave = useCallback(() => {
    setGlow((g) => ({ ...g, active: false }));
  }, []);

  const handleKeyDown = (e) => {
    playKeyClick();
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <Tag className={className} onKeyDown={handleKeyDown} {...props} />
      <div
        className="absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-300"
        style={{
          opacity: glow.active ? 1 : 0,
          background: `radial-gradient(200px circle at ${glow.x}% ${glow.y}%, rgb(var(--color-surface) / 0.06), transparent 60%)`,
        }}
      />
    </div>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    playSubmit();
    setSending(true);
    try {
      await emailjs.send(
        "service_7b0sj38",
        "template_te2y184",
        {
          from_name: form.name,
          from_email: form.email,
          message: form.message,
          to_email: "durvesh.danve@gmail.com",
        },
        "UZJSaNNg5kzCndvqP"
      );
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const socials = [
    { href: socialMediaLinks.github, icon: FaGithub, label: "GitHub" },
    { href: socialMediaLinks.linkedin, icon: FaLinkedinIn, label: "LinkedIn" },
    { href: `mailto:${socialMediaLinks.gmail}`, icon: HiMail, label: "Email" },
    { href: socialMediaLinks.instagram, icon: FaInstagram, label: "Instagram" },
  ];

  const inputClasses =
    "w-full px-4 py-3 rounded-xl bg-surface/[0.03] border border-surface/[0.06] text-fg text-sm placeholder:text-fg-ghost focus:border-surface/[0.15] focus:outline-none transition-colors duration-300";

  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.1 }}
    >
      <motion.div variants={blurIn("up")} className="mb-14">
        <span className="section-label">Say hello</span>
        <h2 className="section-title">
          <ScrambleText
            text={contactPageData.contactSection.title}
            hover={true}
          />
        </h2>
        <p className="section-subtitle">
          {contactPageData.contactSection.description}
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-5 gap-12 max-w-4xl">
        {/* Left — info */}
        <motion.div
          variants={blurIn("left", 0.1)}
          className="lg:col-span-2 flex flex-col justify-between"
        >
          <div className="space-y-5 mb-10">
            <div>
              <p className="text-xs font-mono text-fg-faint uppercase tracking-wider mb-1">
                Email
              </p>
              <a
                href={`mailto:${socialMediaLinks.gmail}`}
                className="text-sm text-fg-muted hover:text-fg transition-colors duration-300"
              >
                {socialMediaLinks.gmail}
              </a>
            </div>
            <div>
              <p className="text-xs font-mono text-fg-faint uppercase tracking-wider mb-1">
                LinkedIn
              </p>
              <a
                href={socialMediaLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-fg-muted hover:text-fg transition-colors duration-300"
              >
                {greeting.full_name}
              </a>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {socials.map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target={label === "Email" ? undefined : "_blank"}
                rel="noopener noreferrer"
                aria-label={label}
                className="text-fg-faint hover:text-fg transition-colors duration-300"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </motion.div>

        {/* Right — form */}
        <motion.div variants={blurIn("right", 0.2)} className="lg:col-span-3">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="name"
                className="block text-xs font-mono text-fg-faint uppercase tracking-wider mb-2"
              >
                Name
              </label>
              <SpotlightInput
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className={inputClasses}
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-mono text-fg-faint uppercase tracking-wider mb-2"
              >
                Email
              </label>
              <SpotlightInput
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className={inputClasses}
              />
            </div>
            <div>
              <label
                htmlFor="message"
                className="block text-xs font-mono text-fg-faint uppercase tracking-wider mb-2"
              >
                Message
              </label>
              <SpotlightInput
                as="textarea"
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="What's on your mind?"
                rows={5}
                required
                className={`${inputClasses} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-fg text-bg font-medium text-sm rounded-full hover:bg-fg/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {sent ? "Sent!" : sending ? "Sending..." : "Send Message"}
              {!sending && !sent && (
                <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                  &rarr;
                </span>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Contact;
